import { React, useState, useEffect } from "react";
import styles from "./registrationPage.module.css";
import logo from "./logo.png";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";

// Import emailRegex and validatePassword from utils.js
import { emailRegex, validatePassword } from "../../../utilities/utils";

const apiUrl = process.env.REACT_APP_URL;

const RegistrationPage = () => {
  /* The Registration Page is a public page that allows a user to register to the website. The user needs to choose a username and a password, enter an email address, and in addition enter the following information: first name, last name, birthdate, native language, language he / she wants to learn, requested cooperation type (language exchange / learn together the same language / both), and gender. The entered information is validated, and if valid - a new user is created and an email is sent to the user, to confirm the entered email address. */

  const [regConfirmation, setRegConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    password: "",
    passwordCheck: "",
    email: "",
    birthdate: "",
    nativeLanguage: "",
    learnedLanguage: "",
    cooperationType: "",
    gender: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordCheckError, setPasswordCheckError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [usernameError, setUsernameError] = useState("");
  const [birthdateError, setBirthdateError] = useState("");

  const [languages, setLanguages] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [feedback, setFeedback] = useState("");

  /**
   * Handle change of the date field by the user
   * @param {*} date - the new date entered by the user
   */
  const handleDateChange = (date) => {
    const today = new Date();
    const minAgeDate = new Date(
      today.getFullYear() - 14,
      today.getMonth(),
      today.getDate()
    );

    if (!date) {
      setSelectedDate(null);
      setBirthdateError("");
      setFormData((prevData) => ({
        ...prevData,
        birthdate: "", // Clear the birthdate in form data
      }));
    } else if (date > today) {
      setBirthdateError("Date cannot be in the future");
    } else if (date > minAgeDate) {
      setBirthdateError("You must be at least 14 years old");
    } else {
      setBirthdateError("");
      setSelectedDate(date);
      const formattedDate = format(date, "yyyy-MM-dd"); // Format the date as yyyy-MM-dd
      setFormData((prevData) => ({
        ...prevData,
        birthdate: formattedDate,
      }));
    }
  };

  // fetch languages data from the server
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const languagesRes = await axios.get(`${apiUrl}/auth/languages`);

        if (languagesRes.status === 200) {
          setLanguages(languagesRes.data.languages);
        }
      } catch (e) {
        console.error("Failed to fetch languages data", e);
      }
    };
    fetchLanguages();
  }, []);

  /**
   * Check whether a username exists on the website.
   * @param {*} username - the username to check for existance.
   * @returns true if the username exists, false otherwise
   */
  const doesUsernameExist = async (username) => {
    try {
      const res = await axios.get(`${apiUrl}/auth/username_check/${username}`);
      if (res.status === 200) {
        if (res.data.success) {
          return true;
        } else {
          return false;
        }
      } else {
        throw new Error("Error checking username");
      }
    } catch (error) {
      throw new Error("Error checking username");
    }
  };

  /**
   * Handle changes of text input fields by the user
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    if (name === "username") {
      setUsernameError("");
    } else if (name === "password") {
      setPasswordError("");
    } else if (name === "passwordCheck") {
      setPasswordCheckError("");
    } else if (name === "email") {
      setEmailError("");
    }
  };

  /**
   * Handle a blur event (moving focus to another element) of a text input field.
   */
  const handleBlur = async (e) => {
    const { name, value } = e.target;

    if (name === "username") {
      if (!value) {
        setUsernameError("");
        return;
      }
      try {
        const exists = await doesUsernameExist(value);
        if (exists) {
          setUsernameError("Username already exists");
        } else {
          setUsernameError("");
        }
      } catch (error) {
        console.error("Error checking username", error);
        setUsernameError("Error checking username");
      }
    } else if (name === "password") {
      if (!value) {
        setPasswordError("");
        return;
      }
      const error = validatePassword(formData.password);
      if (error) {
        setPasswordError(error);
      } else {
        setPasswordError("");
      }
    } else if (name === "passwordCheck") {
      if (!value) {
        setPasswordCheckError("");
        return;
      }
      if (formData.password && formData.password !== formData.passwordCheck) {
        setPasswordCheckError("Passwords should be equal");
      } else {
        setPasswordCheckError("");
      }
    } else if (name === "email") {
      if (!value) {
        setEmailError("");
        return;
      }
      if (!emailRegex.test(value)) {
        setEmailError("Invalid email format");
      } else {
        try {
          const res = await axios.get(`${apiUrl}/auth/email_check/${value}`);
          if (res.status === 200) {
            if (res.data.success) {
              setEmailError("Email already exists");
            } else {
              setEmailError("");
            }
          } else {
            setEmailError("Error checking email");
          }
        } catch (error) {
          console.error("Failed to check email", error);
          setEmailError("Error checking email");
        }
      }
    }
  };

  /**
   * Handles the submission of the registration form - validate the entered data, and if valid - create a new user in the database, and send a confirmation email to the user.
   */
  async function handleRegSubmit(e) {
    e.preventDefault();

    // Check for empty fields or validation errors
    const {
      username,
      firstName,
      lastName,
      password,
      passwordCheck,
      email,
      birthdate,
      nativeLanguage,
      learnedLanguage,
      cooperationType,
      gender,
    } = formData;

    if (
      !username ||
      !firstName ||
      !lastName ||
      !password ||
      !passwordCheck ||
      !email ||
      !birthdate ||
      !nativeLanguage ||
      !learnedLanguage ||
      !cooperationType ||
      !gender
    ) {
      setFeedback("Please fill in all fields.");
      return;
    }

    // Check for validation errors
    if (
      passwordError ||
      passwordCheckError ||
      emailError ||
      usernameError ||
      birthdateError
    ) {
      setFeedback("Please correct the errors before submitting.");
      return;
    }

    try {
      const user = {
        username,
        firstName,
        lastName,
        password,
        email,
        birthdate,
        nativeLanguage,
        learnedLanguage,
        cooperationType,
        gender,
      };

      const res = await axios.post(`${apiUrl}/auth/register`, {
        user,
      });

      if (res.status === 200 && res.data.success) {
        setRegConfirmation(true);
      }
    } catch (e) {
      console.error("Registration failed", e);
    }
  }

  return (
    <>
      {!regConfirmation ? (
        <main className={styles.main}>
          <form className={styles.registerForm} onSubmit={handleRegSubmit}>
            <figure className={styles.logoFig}>
              <img src={logo} alt="Logo" />
            </figure>

            {/* Username input */}
            <div className={styles.inputContainer}>
              <input
                type="text"
                name="username"
                placeholder="Username"
                className={styles.inputWithIcon}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {usernameError && (
                <p className={styles.errorText}>{usernameError}</p>
              )}
            </div>

            {/* Email address */}

            <div className={styles.inputContainer}>
              <input
                type="email"
                name="email"
                placeholder="Email Address"
                className={styles.inputWithIcon}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {emailError && <p className={styles.errorText}>{emailError}</p>}
            </div>

            {/* Password input */}
            <div className={styles.inputContainer}>
              <input
                type="password"
                name="password"
                placeholder="Password"
                className={styles.inputWithIcon}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {passwordError && (
                <p className={styles.errorText}>{passwordError}</p>
              )}
            </div>

            {/* Confirm password input */}

            <div className={styles.inputContainer}>
              <input
                type="password"
                name="passwordCheck"
                placeholder="Confirm Password"
                className={styles.inputWithIcon}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
              {passwordCheckError && (
                <p className={styles.errorText}>{passwordCheckError}</p>
              )}
            </div>

            {/* First name */}

            <div className={styles.inputContainer}>
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                className={styles.inputWithIcon}
                onChange={handleChange}
                required
              />
            </div>

            {/* Last name */}

            <div className={styles.inputContainer}>
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                className={styles.inputWithIcon}
                onChange={handleChange}
                required
              />
            </div>

            {/* Birthdate */}

            <div className={styles.inputContainer}>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                placeholderText="Birthdate"
                maxDate={new Date()} // Disable future dates
                className={`${styles.inputWithIcon} ${styles.datePicker}`}
                required
              />
              {birthdateError && (
                <p className={styles.errorText}>{birthdateError}</p>
              )}
            </div>

            {/* Native language dropdown */}
            <div className={styles.inputContainer}>
              <select
                name="nativeLanguage"
                className={styles.inputWithIcon}
                onChange={handleChange}
                required
              >
                <option value="">Select Native Language</option>
                {languages.map((languageObj, index) => (
                  <option key={index} value={languageObj.language}>
                    {languageObj.language}
                  </option>
                ))}
              </select>
            </div>

            {/* Learned language dropdown */}
            <div className={styles.inputContainer}>
              <select
                name="learnedLanguage"
                className={styles.inputWithIcon}
                onChange={handleChange}
                required
              >
                <option value="">Select Learned Language</option>
                {languages.map((languageObj, index) => (
                  <option key={index} value={languageObj.language}>
                    {languageObj.language}
                  </option>
                ))}
              </select>
            </div>

            {/* Cooperation type */}

            <div className={styles.inputContainer}>
              <select
                name="cooperationType"
                className={styles.inputWithIcon}
                onChange={handleChange}
                required
              >
                <option value="">Cooperation Type</option>
                <option value="Exchange">Exchange</option>
                <option value="Together">Learn Together</option>
                <option value="Both">Both</option>
              </select>
            </div>

            {/* Gender */}

            <div className={styles.inputContainer}>
              <select
                name="gender"
                className={styles.inputWithIcon}
                onChange={handleChange}
                required
              >
                <option value="">Gender</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <button className={styles.registerBtn} type="submit">
              Registration
            </button>

            <div className={styles.changeMode}>
              <Link to="/login" className={styles.customLink}>
                Sign in
              </Link>
            </div>
          </form>
          <div className={styles.feedbackEl}>
            <p
              className={`${styles.feedbackContent} ${
                feedback ? styles.showEl : styles.hideEl
              }`}
            >
              {feedback}
            </p>
          </div>{" "}
        </main>
      ) : (
        <div className={styles.confirmationReg}>
          <p>
            Dear {formData.username}, Thank you for your registration! We have
            sent a verification email to {formData.email}. Please check your
            inbox and click on the verification link in the email we have send
            you, in order to validate your email address and complete your
            registration.
          </p>
        </div>
      )}
    </>
  );
};

export default RegistrationPage;
